
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

interface Match {
  id: string;
  candidateId: string;
  candidateName: string;
  jobId: string;
  jobTitle: string;
  score: number;
}

export const useCompanyMatchesList = (companyId: string | undefined) => {
  const [matches, setMatches] = useState<Match[]>([]);
  const [matchesLoading, setMatchesLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState<'list' | 'card'>('list');
  
  useEffect(() => {
    const fetchMatches = async () => {
      if (!companyId) return;
      
      try {
        setMatchesLoading(true);
        
        // Get jobs for this company to find matches
        const { data: jobs, error: jobsError } = await supabase
          .from('jobs')
          .select('id, title')
          .eq('company_id', companyId);
        
        if (jobsError) throw jobsError;
        
        if (!jobs || jobs.length === 0) {
          setMatches([]);
          return;
        }
        
        const jobIds = jobs.map(job => job.id);
        
        // Get matches for these jobs
        const { data: matchesData, error: matchesError } = await supabase
          .from('matches')
          .select(`
            id,
            score,
            job_id,
            candidate_id,
            candidates(name),
            jobs(title)
          `)
          .in('job_id', jobIds);
        
        if (matchesError) throw matchesError;
        
        if (matchesData) {
          const transformedMatches: Match[] = matchesData.map(match => ({
            id: match.id,
            candidateId: match.candidate_id || '',
            candidateName: match.candidates?.name || 'Unknown Candidate',
            jobId: match.job_id || '',
            jobTitle: match.jobs?.title || 'Unknown Position',
            score: match.score,
          }));
          
          setMatches(transformedMatches);
        }
      } catch (error) {
        console.error("Error fetching company matches:", error);
        
        // Mock data
        setMatches([
          {
            id: '1',
            candidateId: '101',
            candidateName: 'John Doe',
            jobId: '201',
            jobTitle: 'Frontend Developer',
            score: 85
          },
          {
            id: '2',
            candidateId: '102',
            candidateName: 'Jane Smith',
            jobId: '202',
            jobTitle: 'UX Designer',
            score: 92
          }
        ]);
      } finally {
        setMatchesLoading(false);
      }
    };
    
    fetchMatches();
  }, [companyId]);
  
  // Filter matches by search term
  const filteredMatches = matches.filter(match => {
    if (!searchTerm) return true;
    
    const term = searchTerm.toLowerCase();
    return (
      match.candidateName.toLowerCase().includes(term) ||
      match.jobTitle.toLowerCase().includes(term)
    );
  });
  
  return {
    matches: filteredMatches,
    matchesLoading,
    searchTerm,
    setSearchTerm,
    viewMode,
    setViewMode
  };
};
